"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.jaccard = exports.SubjectKeySchema = exports.SubjectKind = void 0;
exports.subjectSignalsFromDesignSignals = subjectSignalsFromDesignSignals;
exports.subjectKey = subjectKey;
exports.signalSectionSlug = signalSectionSlug;
exports.sectionSlug = sectionSlug;
exports.actionVerbClass = actionVerbClass;
exports.quantityFingerprint = quantityFingerprint;
exports.textJaccard = textJaccard;
exports.normalizeWords = normalizeWords;
exports.normalizeCitationWhitespace = normalizeCitationWhitespace;
exports.normalizedCitationIncludes = normalizedCitationIncludes;
exports.quantityTokens = quantityTokens;
const z = require("zod");
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
function subjectSignalsFromDesignSignals(signals) {
    return signals.map((signal) => ({ id: String(signal.id), name: signal.name }));
}
var SubjectKind;
(function (SubjectKind) {
    SubjectKind["Signal"] = "signal";
    SubjectKind["Pin"] = "pin";
    SubjectKind["Rail"] = "rail";
    SubjectKind["Layout"] = "layout";
    SubjectKind["Sequencing"] = "sequencing";
    SubjectKind["Thermal"] = "thermal";
    SubjectKind["Device"] = "device";
})(SubjectKind || (exports.SubjectKind = SubjectKind = {}));
exports.SubjectKeySchema = z
    .object({
    kind: z.nativeEnum(SubjectKind),
    ref: z.string(),
})
    .strict();
const SIGNAL_SLUG_PREFIX = 'signal';
const ACTION_VERBS = {
    connect: ['connect', 'connected', 'tie', 'tied', 'hold', 'held', 'attach', 'link'],
    disconnect: ['disconnect', 'leave open', 'float', 'do not connect'],
    place: ['place', 'locate', 'position', 'route'],
    configure: ['configure', 'set', 'select', 'program'],
    limit: ['limit', 'keep', 'ensure', 'maintain', 'must', 'shall'],
    avoid: ['avoid', 'prevent', 'never', 'must not', 'shall not', 'do not'],
    use: ['use', 'provide', 'add', 'include'],
};
const VOLT = { canonical: 'v', declaration: sigma_engine_shared_1.CommonParameterUnits.voltage };
const AMP = { canonical: 'a', declaration: sigma_engine_shared_1.CommonParameterUnits.current };
const OHM = { canonical: 'ohm', declaration: sigma_engine_shared_1.CommonParameterUnits.resistance };
const FARAD = { canonical: 'f', declaration: sigma_engine_shared_1.CommonParameterUnits.capacitance };
const HERTZ = { canonical: 'hz', declaration: sigma_engine_shared_1.CommonParameterUnits.frequency };
const SECOND = { canonical: 's', declaration: sigma_engine_shared_1.CommonParameterUnits.time };
const SI_UNIT_SPELLINGS = {
    v: VOLT,
    mv: VOLT,
    a: AMP,
    ma: AMP,
    ua: AMP,
    µa: AMP,
    μa: AMP,
    ohm: OHM,
    ω: OHM,
    kohm: OHM,
    kω: OHM,
    mohm: OHM,
    mω: OHM,
    uf: FARAD,
    µf: FARAD,
    μf: FARAD,
    nf: FARAD,
    pf: FARAD,
    hz: HERTZ,
    khz: HERTZ,
    mhz: HERTZ,
    s: SECOND,
    ms: SECOND,
    us: SECOND,
    µs: SECOND,
    μs: SECOND,
    ns: SECOND,
    ps: SECOND,
    fs: SECOND,
};
const NON_SI_UNIT_SCALE = {
    '%': { canonical: 'pct', multiplier: 1 },
    percent: { canonical: 'pct', multiplier: 1 },
    degc: { canonical: 'degc', multiplier: 1 },
    '°c': { canonical: 'degc', multiplier: 1 },
    degf: { canonical: 'degf', multiplier: 1 },
    '°f': { canonical: 'degf', multiplier: 1 },
    k: { canonical: 'k', multiplier: 1 },
    m: { canonical: 'm', multiplier: 1 },
    cm: { canonical: 'm', multiplier: 1e-2 },
    mm: { canonical: 'm', multiplier: 1e-3 },
    mil: { canonical: 'm', multiplier: 2.54e-5 },
    in: { canonical: 'm', multiplier: 0.0254 },
    inch: { canonical: 'm', multiplier: 0.0254 },
    inches: { canonical: 'm', multiplier: 0.0254 },
    db: { canonical: 'db', multiplier: 1 },
};
const UNIT_PATTERN = [...Object.keys(SI_UNIT_SPELLINGS), ...Object.keys(NON_SI_UNIT_SCALE)]
    .sort((a, b) => b.length - a.length)
    .map((unit) => unit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');
const NUMBER_PATTERN = '-?(?:\\d+(?:\\.\\d+)?|\\.\\d+)(?:e[+-]?\\d+)?';
const QUANTITY_PATTERN = new RegExp(`(${NUMBER_PATTERN})\\s*(${UNIT_PATTERN})(?![a-z0-9µω°%])`, 'gi');
const BARE_TOLERANCE_PATTERN = new RegExp(`(?:±|\\+\\s*\\/\\s*-)\\s*(${NUMBER_PATTERN})\\s*(${UNIT_PATTERN})?`, 'gi');
const ASYMMETRIC_TOLERANCE_PATTERN = new RegExp(`\\+\\s*(${NUMBER_PATTERN})\\s*\\/\\s*-\\s*(${NUMBER_PATTERN})\\s*(${UNIT_PATTERN})?`, 'gi');
function subjectKey(subjectRaw, signals) {
    const normalized = normalizeWords(subjectRaw);
    const signal = earliestNamedSignal(normalized, signals);
    if (signal !== undefined) {
        return { kind: SubjectKind.Signal, ref: signalSubjectRef(signal) };
    }
    const pin = normalized.match(/\b(?:pin|ball|pad)\s*([a-z]?\d+)\b/i);
    if (pin?.[1] !== undefined) {
        return { kind: SubjectKind.Pin, ref: pin[1].toLowerCase() };
    }
    if (/\b(sequence|sequencing|power.?up|power.?down|startup|shutdown)\b/.test(normalized)) {
        return { kind: SubjectKind.Sequencing, ref: 'power' };
    }
    if (/\b(vcc|vdd|vss|gnd|supply|rail|power)\b/.test(normalized)) {
        return { kind: SubjectKind.Rail, ref: 'power' };
    }
    if (/\b(layout|pcb|routing|trace|placement)\b/.test(normalized)) {
        return { kind: SubjectKind.Layout, ref: 'layout' };
    }
    if (/\b(thermal|temperature|heatsink|junction)\b/.test(normalized)) {
        return { kind: SubjectKind.Thermal, ref: 'thermal' };
    }
    return { kind: SubjectKind.Device, ref: normalized || 'device' };
}
function signalSectionSlug(ref) {
    const slugged = ref
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    if (slugged !== '') {
        return slugged;
    }
    const codePoints = [];
    for (const character of ref) {
        const code = character.codePointAt(0);
        if (code !== undefined) {
            codePoints.push(`u${code.toString(16)}`);
        }
    }
    return [SIGNAL_SLUG_PREFIX, ...codePoints].join('-');
}
function sectionSlug(key) {
    switch (key.kind) {
        case SubjectKind.Signal:
            return signalSectionSlug(key.ref);
        case SubjectKind.Pin:
            return `pin-${kebabFragment(key.ref)}`;
        case SubjectKind.Rail:
            return 'power';
        case SubjectKind.Layout:
            return 'layout';
        case SubjectKind.Sequencing:
            return 'power-sequencing';
        case SubjectKind.Thermal:
            return 'thermal';
        case SubjectKind.Device:
            return 'device';
    }
}
function actionVerbClass(text) {
    const normalized = normalizeWords(text);
    const best = Object.entries(ACTION_VERBS)
        .flatMap(([action, markers]) => markers.map((marker) => ({ action, marker: normalizeWords(marker) })))
        .filter(({ marker }) => phraseOccurs(normalized, marker))
        .sort((left, right) => right.marker.length - left.marker.length || left.action.localeCompare(right.action))[0];
    return best?.action ?? normalized.split(' ')[0] ?? 'other';
}
function quantityFingerprint(text) {
    return quantityTokens(text).join('|');
}
function textJaccard(left, right) {
    const a = wordSet(normalizeWords(left));
    const b = wordSet(normalizeWords(right));
    const unionSize = new Set([...a, ...b]).size;
    if (unionSize === 0) {
        return 1;
    }
    let intersection = 0;
    for (const token of a) {
        if (b.has(token)) {
            intersection += 1;
        }
    }
    return intersection / unionSize;
}
exports.jaccard = textJaccard;
function normalizeWords(value) {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9µω]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
function normalizeCitationWhitespace(value) {
    return value.replace(/\s+/g, ' ').trim();
}
function normalizedCitationIncludes(source, quote) {
    const normalizedSource = normalizeCitationWhitespace(source);
    const fragments = quote
        .split(/\[(?:\.\.\.|…)\]/)
        .map(normalizeCitationWhitespace)
        .filter(Boolean);
    let cursor = 0;
    for (const fragment of fragments) {
        const match = normalizedSource.indexOf(fragment, cursor);
        if (match < 0) {
            return false;
        }
        cursor = match + fragment.length;
    }
    return fragments.length > 0;
}
function quantityTokens(text) {
    const quantities = [];
    for (const match of text.matchAll(QUANTITY_PATTERN)) {
        const normalized = normalizeQuantity(match[1], match[2]);
        if (normalized !== null) {
            quantities.push(normalized);
        }
    }
    for (const match of text.matchAll(BARE_TOLERANCE_PATTERN)) {
        if (match[2] === undefined) {
            const numeric = Number(match[1]);
            if (Number.isFinite(numeric)) {
                quantities.push(`tol:${formatQuantity(numeric)}`);
            }
        }
    }
    for (const match of text.matchAll(ASYMMETRIC_TOLERANCE_PATTERN)) {
        const [high, low, unit] = [match[1], match[2], match[3]];
        if (high === undefined || low === undefined) {
            continue;
        }
        const normalizedHigh = unit === undefined ? formatQuantity(Number(high)) : normalizeQuantity(high, unit);
        const normalizedLow = unit === undefined ? formatQuantity(Number(low)) : normalizeQuantity(low, unit);
        if (normalizedHigh !== null && normalizedLow !== null) {
            quantities.push(`tol:+${normalizedHigh}/-${normalizedLow}`);
        }
    }
    return [...new Set(quantities)].sort();
}
function kebabFragment(value) {
    const slugged = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    return slugged === '' ? 'device' : slugged;
}
function normalizeQuantity(rawNumeric, rawUnit) {
    const numeric = Number(rawNumeric);
    if (rawNumeric === undefined || rawUnit === undefined || !Number.isFinite(numeric)) {
        return null;
    }
    const siUnit = SI_UNIT_SPELLINGS[rawUnit.toLowerCase()];
    if (siUnit !== undefined) {
        try {
            const scaled = sigma_engine_shared_1.Utils.parseNumericalSIValue(`${rawNumeric}${rawUnit}`, siUnit.declaration);
            return Number.isFinite(scaled) ? `${formatQuantity(scaled)}${siUnit.canonical}` : null;
        }
        catch {
            return null;
        }
    }
    const unit = NON_SI_UNIT_SCALE[rawUnit.toLowerCase()];
    return unit === undefined ? null : `${formatQuantity(numeric * unit.multiplier)}${unit.canonical}`;
}
function signalSubjectRef(signal) {
    const candidates = [signal.name, signal.id];
    const worded = candidates.find((candidate) => normalizeWords(candidate) !== '');
    return worded ?? candidates.find((candidate) => candidate.trim() !== '') ?? '';
}
function earliestNamedSignal(normalized, signals) {
    const padded = ` ${normalized} `;
    let best;
    for (const signal of signals) {
        for (const name of [signal.id, signal.name, ...(signal.aliases ?? [])].map(normalizeWords)) {
            const position = name === '' ? -1 : padded.indexOf(` ${name} `);
            if (position === -1) {
                continue;
            }
            const closer = best === undefined || position < best.position;
            const longerAtSamePosition = best !== undefined && position === best.position && name.length > best.length;
            if (closer || longerAtSamePosition) {
                best = { signal, position, length: name.length };
            }
        }
    }
    return best?.signal;
}
function wordSet(value) {
    return new Set(value.split(' ').filter(Boolean));
}
function phraseOccurs(source, phrase) {
    return ` ${source} `.includes(` ${phrase} `);
}
function formatQuantity(value) {
    return Number(value.toPrecision(12)).toString();
}
//# sourceMappingURL=rule-normalization.js.map